import React, { type ReactElement } from 'react';
import { useParams } from 'react-router-dom';

import { ReactComponent as GatewayIcon } from '@assets/svg/devices/ic-gateway.svg';
import { ReactComponent as MoteBatteriesIcon } from '@assets/svg/devices/ic-mote-batteries.svg';
import { ReactComponent as MoteIcon } from '@assets/svg/devices/ic-mote.svg';
import { ReactComponent as NodeIcon } from '@assets/svg/devices/ic-node.svg';
import { ReactComponent as RouterIcon } from '@assets/svg/devices/ic-router.svg';
import { ReactComponent as SensorIcon } from '@assets/svg/devices/ic-sensor.svg';
import { ReactComponent as TachometerIcon } from '@assets/svg/devices/ic-tachometer.svg';
import { ReactComponent as UAIcon } from '@assets/svg/devices/ic-universal-adapter.svg';

import useTranslation from '@hooks/useTranslation';

import type { TProductGuideEquipment } from '@pages/HelpAndResources/model/productGuideList';
import { useProductGuidesQuery } from '@pages/HelpAndResources/services/useProductGuidesQuery';

import ModalHeader from '../ModalHeader/ModalHeader';
import ProductGuidesSkeleton from './ProductGuidesSkeleton';
import ProductGuideItem from './components/ProductGuideItem';
import style from './style.module.scss';

const ProductGuidesModal = () => {
  const { t } = useTranslation();
  const { customerId } = useParams();
  const { data, isLoading } = useProductGuidesQuery(customerId);

  const guides = data ? [...data.otherEquipment, ...data.moteEquipment, ...data.batteries] : [];

  const guideIcon: Record<TProductGuideEquipment, ReactElement> = {
    gateway: <GatewayIcon />,
    sensor: <SensorIcon />,
    node: <NodeIcon />,
    repeater: <RouterIcon />,
    tachometer: <TachometerIcon />,
    universal_adapter: <UAIcon />,
    mote_sm1_sm2_sm6: <MoteIcon />,
    mote_sm7: <MoteIcon />,
    mote_batteries: <MoteBatteriesIcon />,
  };

  const guideName: Record<TProductGuideEquipment, string> = {
    gateway: 'Gateway',
    sensor: 'Tethered sensor',
    node: 'Node',
    repeater: 'Repeater',
    tachometer: 'Tachometer',
    universal_adapter: 'Universal Adapter',
    mote_sm1_sm2_sm6: 'Sensor Mote: SM1, SM2, and SM6 Models',
    mote_sm7: 'Sensor Mote: SM7 Models',
    mote_batteries: 'Mote Batteries',
  };

  return (
    <div className={style.modalProductGuides}>
      <ModalHeader title={t('Product Guides')} />
      <div className={style.modalBody}>
        {isLoading ? (
          <ProductGuidesSkeleton />
        ) : (
          guides.map((guide) => (
            <ProductGuideItem
              key={guide.id}
              icon={guideIcon[guide.equipmentType]}
              name={guideName[guide.equipmentType]}
              url={guide.url}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default ProductGuidesModal;
